import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { theme } from '../theme'
import { displayTimeZone, displayTzAbbrev } from '../utils/datetime'

/**
 * Header-right identity + live clock.
 *
 * The clock renders in the SAME zone as every event timestamp in the UI
 * (utils/datetime's displayTimeZone — the browser's IANA zone, falling back to
 * Pacific), and shows that zone's abbreviation. That is the point: an operator
 * comparing "now" against a run's Submitted time must not have to reconcile two
 * different timezones.
 */
export function UserClock() {
  // Consume the shared context rather than fetching /auth/me again — one
  // authoritative role for the whole app, and one request instead of two.
  const { user, role, enforcementActive: enforcing } = useAuth()
  const [now, setNow] = useState(() => new Date())

  // Tick every second, aligned so the displayed second changes on the second
  // rather than drifting by however long the mount happened to land at.
  useEffect(() => {
    let timer: number
    const tick = () => {
      setNow(new Date())
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000))
    }
    timer = window.setTimeout(tick, 1000 - (Date.now() % 1000))
    return () => window.clearTimeout(timer)
  }, [])

  const label =
    user?.name || user?.preferred_username || user?.email || user?.sub || null

  const time = now.toLocaleTimeString('en-US', {
    timeZone: displayTimeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const date = now.toLocaleDateString('en-US', {
    timeZone: displayTimeZone,
    month: 'short',
    day: 'numeric',
  })

  return (
    <div
      style={{
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        fontSize: '0.82rem',
        color: theme.onPrimary,
        whiteSpace: 'nowrap',
      }}
    >
      {label && (
        <span
          title={user?.email ? `Signed in as ${user.email}` : 'Signed in'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', opacity: 0.9 }}
        >
          <span aria-hidden="true">👤</span>
          <span>{label}</span>
        </span>
      )}
      {/* Only shown while enforcement is live — a role badge with nothing behind
          it would imply a restriction that is not actually applied. */}
      {enforcing && role && (
        <span
          title={
            role === 'admin'
              ? 'You have the admin role: full access'
              : 'You have the viewer role: read-only access'
          }
          style={{
            padding: '0.1rem 0.45rem',
            borderRadius: 10,
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            border: `1px solid ${role === 'admin' ? '#4caf50' : theme.textMuted}`,
            color: role === 'admin' ? '#4caf50' : theme.textMuted,
          }}
        >
          {role}
        </span>
      )}
      <span
        title={`Current time (${displayTimeZone}) — same timezone as all event timestamps`}
        style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.9 }}
      >
        {date} {time} {displayTzAbbrev}
      </span>
      <a
        href="/auth/logout"
        style={{ color: theme.onPrimary, opacity: 0.7, textDecoration: 'none', fontSize: '0.78rem' }}
        title="Sign out"
      >
        Sign out
      </a>
    </div>
  )
}

export default UserClock
